import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Building2, Users, Briefcase, DollarSign, Package, Clock,
  CheckCircle, XCircle, Eye, ChevronRight, Mail, Phone,
  LogOut, RefreshCw, Shield, AlertTriangle, Plus, Edit, Trash2,
  Ban, Calendar, CreditCard, Search, MoreVertical, UserPlus,
  CalendarPlus, History, Settings, Key, HelpCircle, MessageSquare,
  Send, FileText, Smartphone
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SuperAdminDashboard = ({ user, setUser, defaultTab }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [pendingItems, setPendingItems] = useState([]);
  const [recentActivity, setRecentActivity] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [roles, setRoles] = useState({ predefined_roles: {}, custom_roles: [] });
  const [subscriptions, setSubscriptions] = useState([]);
  const [auditLogs, setAuditLogs] = useState({ logs: [], total: 0, page: 1, pages: 1 });
  const [auditFilter, setAuditFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(defaultTab || 'overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [openTicketsCount, setOpenTicketsCount] = useState(0);
  const [urgentTicketsCount, setUrgentTicketsCount] = useState(0);
  
  // Modals
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [showCreateRole, setShowCreateRole] = useState(false);
  const [showCompanyDetails, setShowCompanyDetails] = useState(null);
  const [showExtendModal, setShowExtendModal] = useState(null);
  const [showBanModal, setShowBanModal] = useState(null);

  const token = localStorage.getItem('movemate_token');

  // Update activeTab when defaultTab changes
  useEffect(() => {
    if (defaultTab) {
      setActiveTab(defaultTab);
    }
  }, [defaultTab]);

  // API calls
  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/superadmin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 403) {
        toast.error('Access denied. Superadmin privileges required.');
        navigate('/');
        return;
      }
      const data = await res.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [token, navigate]);

  const fetchCompanies = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/superadmin/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setCompanies(data.companies || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  }, [token]);

  const fetchPendingItems = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/superadmin/pending-items`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setPendingItems(data.items || []);
    } catch (error) {
      console.error('Error fetching pending items:', error);
    }
  }, [token]);

  const fetchRecentActivity = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/superadmin/recent-activity`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setRecentActivity(data);
    } catch (error) {
      console.error('Error fetching recent activity:', error);
    }
  }, [token]);

  const fetchAdmins = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/superadmin/admins`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setAdmins(data.admins || []);
    } catch (error) {
      console.error('Error fetching admins:', error);
    }
  }, [token]);

  const fetchRoles = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/superadmin/roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setRoles(data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  }, [token]);

  const fetchSubscriptions = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/superadmin/subscriptions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setSubscriptions(data.subscriptions || []);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    }
  }, [token]);

  const fetchTicketCounts = useCallback(async () => {
    try {
      // Fetch open tickets
      const openRes = await fetch(`${API_URL}/api/superadmin/tickets?status=open&limit=1`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const openData = await openRes.json();
      setOpenTicketsCount(openData.total || 0);
      
      // Fetch urgent tickets
      const urgentRes = await fetch(`${API_URL}/api/superadmin/tickets?priority=urgent&limit=1`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const urgentData = await urgentRes.json();
      // Count only non-closed urgent tickets
      const urgentOpen = (urgentData.tickets || []).filter(t => t.status !== 'closed' && t.status !== 'resolved').length;
      setUrgentTicketsCount(urgentOpen);
    } catch (error) {
      console.error('Error fetching ticket counts:', error);
    }
  }, [token]);

  const fetchAuditLogs = useCallback(async (page = 1, actionType = '') => {
    try {
      let url = `${API_URL}/api/superadmin/audit-log?page=${page}&limit=50`;
      if (actionType) {
        url += `&action_type=${actionType}`;
      }
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setAuditLogs(data);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchStats(),
        fetchCompanies(),
        fetchPendingItems(),
        fetchRecentActivity(),
        fetchAdmins(),
        fetchRoles(),
        fetchSubscriptions(),
        fetchTicketCounts()
      ]);
      setLoading(false);
    };
    loadData();
    
    // Refresh ticket counts every 60 seconds
    const ticketInterval = setInterval(fetchTicketCounts, 60000);
    return () => clearInterval(ticketInterval);
  }, [fetchStats, fetchCompanies, fetchPendingItems, fetchRecentActivity, fetchAdmins, fetchRoles, fetchSubscriptions, fetchTicketCounts]);

  // Actions
  const handleBanCompany = async (companyId, reason) => {
    try {
      const res = await fetch(`${API_URL}/api/superadmin/companies/${companyId}/ban?reason=${encodeURIComponent(reason)}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to ban company');
      toast.success('Company banned for 30 days');
      setShowBanModal(null);
      fetchCompanies();
    } catch (error) {
      toast.error('Failed to ban company');
    }
  };

  const handleUnbanCompany = async (companyId) => {
    try {
      const res = await fetch(`${API_URL}/api/superadmin/companies/${companyId}/unban`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to unban company');
      toast.success('Company unbanned');
      fetchCompanies();
    } catch (error) {
      toast.error('Failed to unban company');
    }
  };

  const handleDeleteCompany = async (companyId) => {
    if (!window.confirm('Are you sure? This will PERMANENTLY delete all company data. This cannot be undone!')) return;
    if (!window.confirm('FINAL WARNING: All users, jobs, and data will be deleted forever. Continue?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/superadmin/companies/${companyId}?confirm=true`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to delete company');
      toast.success('Company permanently deleted');
      fetchCompanies();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete company');
    }
  };

  const handleExtendSubscription = async (companyId, months, customDate) => {
    try {
      const params = months ? `months=${months}` : `end_date=${customDate}`;
      const res = await fetch(`${API_URL}/api/superadmin/subscriptions/${companyId}/extend?${params}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to extend subscription');
      const data = await res.json();
      toast.success(`Subscription extended to ${new Date(data.new_end_date).toLocaleDateString()}`);
      setShowExtendModal(null);
      fetchCompanies();
      fetchSubscriptions();
    } catch (error) {
      toast.error('Failed to extend subscription');
    }
  };

  const handleApproveItem = async (itemId) => {
    try {
      const res = await fetch(`${API_URL}/api/superadmin/pending-items/${itemId}/approve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to approve item');
      toast.success('Item approved');
      fetchPendingItems();
      fetchStats();
    } catch (error) {
      toast.error('Failed to approve item');
    }
  };

  const handleRejectItem = async (itemId) => {
    try {
      const res = await fetch(`${API_URL}/api/superadmin/pending-items/${itemId}/reject`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to reject item');
      toast.success('Item rejected');
      fetchPendingItems();
      fetchStats();
    } catch (error) {
      toast.error('Failed to reject item');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('movemate_token');
    localStorage.removeItem('movemate_user');
    setUser(null);
    navigate('/superadmin/login');
  };

  // Filter companies
  const filteredCompanies = companies.filter(c => 
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <Building2 size={18} /> },
    { id: 'companies', label: 'Companies', icon: <Building2 size={18} /> },
    { id: 'subscriptions', label: 'Subscriptions', icon: <CreditCard size={18} /> },
    { id: 'admins', label: 'Admin Users', icon: <Users size={18} /> },
    { id: 'approvals', label: 'Approvals', icon: <Package size={18} />, badge: pendingItems.length > 0 ? pendingItems.length : null },
    { id: 'tickets', label: 'Support Tickets', icon: <HelpCircle size={18} />, badge: openTicketsCount > 0 ? openTicketsCount : null, urgent: urgentTicketsCount > 0 },
    { id: 'messaging', label: 'Messaging', icon: <Mail size={18} /> },
    { id: 'activity', label: 'Activity', icon: <History size={18} /> },
    { id: 'audit', label: 'Audit Log', icon: <Shield size={18} /> },
  ];

  // When rendered from a nested route (defaultTab provided), render only content without nav
  if (defaultTab) {
    return (
      <div data-testid={`superadmin-${defaultTab}`}>
        {activeTab === 'overview' && (
          <OverviewTab 
            stats={stats} 
            pendingItems={pendingItems}
            recentActivity={recentActivity}
            onRefresh={() => {
              fetchStats();
              fetchCompanies();
              fetchPendingItems();
            }}
            setActiveTab={setActiveTab}
          />
        )}
        {activeTab === 'companies' && (
          <CompaniesTab
            companies={filteredCompanies}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onBan={(company) => setShowBanModal(company)}
            onUnban={handleUnbanCompany}
            onDelete={handleDeleteCompany}
            onExtend={(company) => setShowExtendModal(company)}
            onViewDetails={(company) => setShowCompanyDetails(company)}
          />
        )}
        {activeTab === 'subscriptions' && (
          <SubscriptionsTab
            subscriptions={subscriptions}
            onExtend={(sub) => setShowExtendModal({ id: sub.company_id, ...sub })}
          />
        )}
        {activeTab === 'admins' && (
          <AdminUsersTab
            admins={admins}
            roles={roles}
            onCreateAdmin={() => setShowCreateAdmin(true)}
            onCreateRole={() => setShowCreateRole(true)}
            onRefresh={fetchAdmins}
            token={token}
          />
        )}
        {activeTab === 'approvals' && (
          <ApprovalsTab
            pendingItems={pendingItems}
            onApprove={handleApproveItem}
            onReject={handleRejectItem}
          />
        )}
        {activeTab === 'tickets' && <TicketsTab token={token} />}
        {activeTab === 'messaging' && <MessagingTab token={token} companies={companies} />}
        {activeTab === 'activity' && <ActivityTab recentActivity={recentActivity} />}
        {activeTab === 'audit' && (
          <AuditLogTab
            auditLogs={auditLogs}
            auditFilter={auditFilter}
            setAuditFilter={setAuditFilter}
            onFetch={fetchAuditLogs}
          />
        )}
        
        {/* Modals */}
        {showExtendModal && (
          <ExtendSubscriptionModal
            company={showExtendModal}
            onClose={() => setShowExtendModal(null)}
            onExtend={handleExtendSubscription}
          />
        )}
        {showBanModal && (
          <BanCompanyModal
            company={showBanModal}
            onClose={() => setShowBanModal(null)}
            onBan={handleBanCompany}
          />
        )}
        {showCreateAdmin && (
          <CreateAdminModal
            open={showCreateAdmin}
            onClose={() => setShowCreateAdmin(false)}
            roles={roles}
            token={token}
            onSuccess={() => {
              fetchAdmins();
              setShowCreateAdmin(false);
            }}
          />
        )}
        {showCreateRole && (
          <CreateRoleModal
            open={showCreateRole}
            onClose={() => setShowCreateRole(false)}
            token={token}
            onSuccess={() => {
              fetchAdmins();
              setShowCreateRole(false);
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100" data-testid="superadmin-dashboard">
      {/* Header */}
      <header className="bg-slate-900 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="h-8 w-8 text-amber-400" />
            <div>
              <h1 className="text-xl font-bold">MoveMate Superadmin</h1>
              <p className="text-sm text-slate-400">Platform Management</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Ticket Count Badges */}
            {(openTicketsCount > 0 || urgentTicketsCount > 0) && (
              <button 
                onClick={() => setActiveTab('tickets')}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              >
                <HelpCircle size={16} />
                {urgentTicketsCount > 0 && (
                  <span className="flex items-center gap-1 text-red-400 text-sm font-medium">
                    <AlertTriangle size={14} />
                    {urgentTicketsCount} urgent
                  </span>
                )}
                {openTicketsCount > 0 && (
                  <span className="text-blue-300 text-sm font-medium">
                    {openTicketsCount} open
                  </span>
                )}
              </button>
            )}
            <span className="text-sm text-slate-300">{user?.email}</span>
            <Button variant="outline" size="sm" onClick={handleLogout} className="text-white border-white/30 hover:bg-white/10">
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex gap-1 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-4 px-4 font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id 
                    ? 'border-primary text-primary' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.icon}
                {tab.label}
                {tab.badge && (
                  <Badge variant={tab.urgent ? "destructive" : "secondary"} className="ml-1">
                    {tab.badge}
                  </Badge>
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <OverviewTab 
            stats={stats} 
            pendingItems={pendingItems}
            recentActivity={recentActivity}
            onRefresh={() => {
              fetchStats();
              fetchCompanies();
              fetchPendingItems();
            }}
            setActiveTab={setActiveTab}
          />
        )}

        {/* Companies Tab */}
        {activeTab === 'companies' && (
          <CompaniesTab
            companies={filteredCompanies}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onBan={(company) => setShowBanModal(company)}
            onUnban={handleUnbanCompany}
            onDelete={handleDeleteCompany}
            onExtend={(company) => setShowExtendModal(company)}
            onViewDetails={(company) => setShowCompanyDetails(company)}
          />
        )}

        {/* Subscriptions Tab */}
        {activeTab === 'subscriptions' && (
          <SubscriptionsTab
            subscriptions={subscriptions}
            onExtend={(sub) => setShowExtendModal({ id: sub.company_id, ...sub })}
          />
        )}

        {/* Admin Users Tab */}
        {activeTab === 'admins' && (
          <AdminUsersTab
            admins={admins}
            roles={roles}
            onCreateAdmin={() => setShowCreateAdmin(true)}
            onCreateRole={() => setShowCreateRole(true)}
            onRefresh={fetchAdmins}
            token={token}
          />
        )}

        {/* Approvals Tab */}
        {activeTab === 'approvals' && (
          <ApprovalsTab
            pendingItems={pendingItems}
            onApprove={handleApproveItem}
            onReject={handleRejectItem}
          />
        )}

        {/* Support Tickets Tab */}
        {activeTab === 'tickets' && (
          <TicketsTab token={token} />
        )}

        {/* Messaging Tab */}
        {activeTab === 'messaging' && (
          <MessagingTab token={token} companies={companies} />
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <ActivityTab recentActivity={recentActivity} />
        )}

        {/* Audit Log Tab */}
        {activeTab === 'audit' && (
          <AuditLogTab
            auditLogs={auditLogs}
            auditFilter={auditFilter}
            setAuditFilter={setAuditFilter}
            onFetch={fetchAuditLogs}
          />
        )}
      </main>

      {/* Extend Subscription Modal */}
      <ExtendSubscriptionModal
        company={showExtendModal}
        onClose={() => setShowExtendModal(null)}
        onExtend={handleExtendSubscription}
      />

      {/* Ban Company Modal */}
      <BanCompanyModal
        company={showBanModal}
        onClose={() => setShowBanModal(null)}
        onBan={handleBanCompany}
      />

      {/* Create Admin Modal */}
      <CreateAdminModal
        open={showCreateAdmin}
        onClose={() => setShowCreateAdmin(false)}
        roles={roles}
        token={token}
        onSuccess={() => {
          setShowCreateAdmin(false);
          fetchAdmins();
        }}
      />

      {/* Create Role Modal */}
      <CreateRoleModal
        open={showCreateRole}
        onClose={() => setShowCreateRole(false)}
        token={token}
        onSuccess={() => {
          setShowCreateRole(false);
          fetchRoles();
        }}
      />
    </div>
  );
};

// ============== SUB-COMPONENTS ==============

const OverviewTab = ({ stats, pendingItems, recentActivity, onRefresh, setActiveTab }) => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <h2 className="text-2xl font-bold text-gray-900">Platform Overview</h2>
      <Button variant="outline" size="sm" onClick={onRefresh}>
        <RefreshCw className="h-4 w-4 mr-2" />
        Refresh
      </Button>
    </div>

    {/* Stats Cards */}
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      <StatCard icon={<Building2 />} value={stats?.total_companies} label="Companies" color="blue" />
      <StatCard icon={<Users />} value={stats?.total_users} label="Total Users" color="green" />
      <StatCard icon={<Briefcase />} value={stats?.total_jobs} label="Total Jobs" color="purple" />
      <StatCard icon={<Clock />} value={stats?.pending_item_approvals} label="Pending" color="amber" />
      <StatCard icon={<CheckCircle />} value={stats?.active_subscriptions} label="Active Subs" color="emerald" />
      <StatCard icon={<DollarSign />} value={`€${stats?.revenue_this_month?.toFixed(0) || 0}`} label="This Month" color="rose" />
    </div>

    {/* Pending Approvals Alert */}
    {stats?.pending_item_approvals > 0 && (
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
              <div>
                <p className="font-semibold text-amber-800">
                  {stats.pending_item_approvals} inventory item(s) awaiting approval
                </p>
                <p className="text-sm text-amber-600">
                  Review and approve custom inventory items submitted by companies
                </p>
              </div>
            </div>
            <Button onClick={() => setActiveTab('approvals')}>
              Review Now
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    )}

    {/* Quick Stats */}
    <div className="grid md:grid-cols-3 gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Recent Companies</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {(recentActivity?.recent_companies || []).slice(0, 5).map((company) => (
              <div key={company.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <span className="font-medium text-sm">{company.name}</span>
                <span className="text-xs text-gray-500">
                  {new Date(company.created_at).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Recent Jobs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {(recentActivity?.recent_jobs || []).slice(0, 5).map((job) => (
              <div key={job.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <span className="font-medium text-sm">{job.job_number}</span>
                <Badge variant="outline" className="text-xs">{job.status}</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Contact Submissions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {(recentActivity?.recent_contacts || []).slice(0, 5).map((contact) => (
              <div key={contact.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <span className="font-medium text-sm truncate">{contact.name}</span>
                <Badge variant={contact.status === 'new' ? 'default' : 'secondary'} className="text-xs">
                  {contact.status}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
);

const StatCard = ({ icon, value, label, color }) => {
  const colors = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    amber: 'bg-amber-100 text-amber-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    rose: 'bg-rose-100 text-rose-600',
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${colors[color]}`}>
            {React.cloneElement(icon, { className: 'h-5 w-5' })}
          </div>
          <div>
            <p className="text-2xl font-bold">{value || 0}</p>
            <p className="text-xs text-gray-500">{label}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const CompaniesTab = ({ companies, searchTerm, setSearchTerm, onBan, onUnban, onDelete, onExtend, onViewDetails }) => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <h2 className="text-2xl font-bold text-gray-900">Company Management</h2>
      <div className="flex items-center gap-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search companies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 w-64"
          />
        </div>
      </div>
    </div>

    <div className="grid gap-4">
      {companies.map((company) => (
        <Card key={company.id} className={company.is_banned ? 'border-red-300 bg-red-50' : ''}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-lg ${company.is_banned ? 'bg-red-100' : 'bg-primary/10'}`}>
                  <Building2 className={`h-6 w-6 ${company.is_banned ? 'text-red-600' : 'text-primary'}`} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900">{company.name}</h3>
                    {company.is_banned && <Badge variant="destructive">Banned</Badge>}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      {company.email}
                    </span>
                    {company.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        {company.phone}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="text-right mr-4">
                  <div className="flex gap-2">
                    <Badge variant={company.is_active ? 'default' : 'secondary'}>
                      {company.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    {company.subscription && (
                      <Badge variant="outline" className="capitalize">
                        {company.subscription.plan_type?.replace('_', ' ')}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    {company.user_count} users
                  </p>
                </div>
                
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => onViewDetails(company)}>
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => onExtend(company)}>
                    <CalendarPlus className="h-4 w-4" />
                  </Button>
                  {company.is_banned ? (
                    <Button variant="ghost" size="sm" className="text-green-600" onClick={() => onUnban(company.id)}>
                      <CheckCircle className="h-4 w-4" />
                    </Button>
                  ) : (
                    <Button variant="ghost" size="sm" className="text-amber-600" onClick={() => onBan(company)}>
                      <Ban className="h-4 w-4" />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" className="text-red-600" onClick={() => onDelete(company.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  </div>
);

const SubscriptionsTab = ({ subscriptions, onExtend }) => (
  <div className="space-y-6">
    <h2 className="text-2xl font-bold text-gray-900">Subscription Management</h2>
    
    <div className="grid gap-4">
      {subscriptions.map((sub) => (
        <Card key={sub.id}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-lg ${
                  sub.status === 'active' ? 'bg-green-100' : 
                  sub.status === 'trialing' ? 'bg-blue-100' : 'bg-gray-100'
                }`}>
                  <CreditCard className={`h-6 w-6 ${
                    sub.status === 'active' ? 'text-green-600' : 
                    sub.status === 'trialing' ? 'text-blue-600' : 'text-gray-600'
                  }`} />
                </div>
                <div>
                  <h3 className="font-semibold">{sub.company_name || 'Unknown Company'}</h3>
                  <p className="text-sm text-gray-500">{sub.company_email}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <Badge variant="outline" className="capitalize">{sub.plan_type?.replace('_', ' ')}</Badge>
                  <p className="text-xs text-gray-500 mt-1">Plan</p>
                </div>
                <div className="text-center">
                  <Badge variant={sub.status === 'active' ? 'default' : sub.status === 'trialing' ? 'secondary' : 'destructive'}>
                    {sub.status}
                  </Badge>
                  <p className="text-xs text-gray-500 mt-1">Status</p>
                </div>
                <div className="text-center">
                  <p className="font-medium">{sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : 'N/A'}</p>
                  <p className="text-xs text-gray-500">Expires</p>
                </div>
                
                <Button variant="outline" size="sm" onClick={() => onExtend(sub)}>
                  <CalendarPlus className="h-4 w-4 mr-2" />
                  Extend
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  </div>
);

const AdminUsersTab = ({ admins, roles, onCreateAdmin, onCreateRole, onRefresh, token }) => {
  const handleDeleteAdmin = async (adminId) => {
    if (!window.confirm('Delete this admin user?')) return;
    try {
      const res = await fetch(`${API_URL}/api/superadmin/admins/${adminId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('Admin deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete admin');
    }
  };

  const handleResetPassword = async (adminId) => {
    try {
      const res = await fetch(`${API_URL}/api/superadmin/admins/${adminId}/reset-password`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error('Failed');
      toast.success(`New password: ${data.temp_password}`);
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Admin User Management</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCreateRole}>
            <Settings className="h-4 w-4 mr-2" />
            Create Role
          </Button>
          <Button onClick={onCreateAdmin}>
            <UserPlus className="h-4 w-4 mr-2" />
            Add Admin
          </Button>
        </div>
      </div>

      {/* Predefined Roles */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Available Roles</CardTitle>
          <CardDescription>Predefined and custom roles for admin users</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {Object.entries(roles.predefined_roles || {}).map(([key, role]) => (
              <Badge key={key} variant="outline" className="py-1 px-3">
                {role.name} (Level {role.level})
              </Badge>
            ))}
            {(roles.custom_roles || []).map((role) => (
              <Badge key={role.id} variant="secondary" className="py-1 px-3">
                {role.name} (Level {role.level})
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Admin Users List */}
      <div className="grid gap-4">
        {admins.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium">No admin users yet</h3>
              <p className="text-gray-500 mb-4">Create your first admin user</p>
              <Button onClick={onCreateAdmin}>
                <UserPlus className="h-4 w-4 mr-2" />
                Add Admin
              </Button>
            </CardContent>
          </Card>
        ) : (
          admins.map((admin) => (
            <Card key={admin.id}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-primary/10 rounded-lg">
                      <Users className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{admin.first_name} {admin.last_name}</h3>
                      <p className="text-sm text-gray-500">{admin.email}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <Badge variant="outline" className="capitalize">{admin.role?.replace('_', ' ')}</Badge>
                    <Badge variant={admin.is_active ? 'default' : 'secondary'}>
                      {admin.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleResetPassword(admin.id)}>
                        <Key className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteAdmin(admin.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

const ApprovalsTab = ({ pendingItems, onApprove, onReject }) => (
  <div className="space-y-6">
    <h2 className="text-2xl font-bold text-gray-900">Pending Inventory Approvals</h2>
    
    {pendingItems.length === 0 ? (
      <Card>
        <CardContent className="py-12 text-center">
          <CheckCircle className="mx-auto h-12 w-12 text-green-500 mb-4" />
          <h3 className="text-lg font-medium text-gray-900">All caught up!</h3>
          <p className="text-gray-500">No pending items to review.</p>
        </CardContent>
      </Card>
    ) : (
      <div className="grid gap-4">
        {pendingItems.map((item) => (
          <Card key={item.id}>
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-amber-100 rounded-lg">
                    <Package className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{item.name}</h3>
                    <p className="text-sm text-gray-500">
                      Category: {item.category} | Icon: {item.icon}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Submitted by: {item.submitted_by_company_name || 'Unknown'}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => onReject(item.id)}
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    Reject
                  </Button>
                  <Button 
                    size="sm"
                    onClick={() => onApprove(item.id)}
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Approve
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    )}
  </div>
);

const ActivityTab = ({ recentActivity }) => (
  <div className="space-y-6">
    <h2 className="text-2xl font-bold text-gray-900">Recent Activity</h2>
    
    <div className="grid md:grid-cols-3 gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Recent Companies</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {(recentActivity?.recent_companies || []).map((company) => (
              <div key={company.id} className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                <Building2 className="h-4 w-4 text-gray-400" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{company.name}</p>
                  <p className="text-xs text-gray-500">
                    {new Date(company.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Recent Jobs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {(recentActivity?.recent_jobs || []).map((job) => (
              <div key={job.id} className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                <Briefcase className="h-4 w-4 text-gray-400" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{job.job_number}</p>
                  <Badge variant="outline" className="text-xs">{job.status}</Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Contact Submissions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {(recentActivity?.recent_contacts || []).map((contact) => (
              <div key={contact.id} className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                <Mail className="h-4 w-4 text-gray-400" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{contact.name}</p>
                  <p className="text-xs text-gray-500 truncate">{contact.subject}</p>
                </div>
                <Badge variant={contact.status === 'new' ? 'default' : 'secondary'} className="text-xs">
                  {contact.status}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
);

// ============== AUDIT LOG TAB ==============
const AuditLogTab = ({ auditLogs, auditFilter, setAuditFilter, onFetch }) => {
  const [currentPage, setCurrentPage] = useState(1);
  
  useEffect(() => {
    onFetch(1, '');
  }, [onFetch]);

  const handleFilterChange = (value) => {
    setAuditFilter(value);
    setCurrentPage(1);
    onFetch(1, value === 'all' ? '' : value);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    onFetch(page, auditFilter === 'all' ? '' : auditFilter);
  };

  const getActionBadgeColor = (action) => {
    if (action.includes('success')) return 'bg-green-100 text-green-800';
    if (action.includes('failed')) return 'bg-red-100 text-red-800';
    if (action.includes('ban')) return 'bg-orange-100 text-orange-800';
    if (action.includes('unban')) return 'bg-blue-100 text-blue-800';
    if (action.includes('delete')) return 'bg-red-100 text-red-800';
    if (action.includes('extend')) return 'bg-purple-100 text-purple-800';
    return 'bg-gray-100 text-gray-800';
  };

  const formatAction = (action) => {
    return action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Audit Log</h2>
        <div className="flex items-center gap-4">
          <Select value={auditFilter || 'all'} onValueChange={handleFilterChange}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by action" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              <SelectItem value="login_success">Login Success</SelectItem>
              <SelectItem value="login_failed">Login Failed</SelectItem>
              <SelectItem value="company_banned">Company Banned</SelectItem>
              <SelectItem value="company_unbanned">Company Unbanned</SelectItem>
              <SelectItem value="subscription_extended">Subscription Extended</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => onFetch(currentPage, auditFilter === 'all' ? '' : auditFilter)}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="h-5 w-5 text-amber-500" />
            Security & Admin Activity
          </CardTitle>
          <CardDescription>
            All superadmin actions are logged and monitored for security compliance
          </CardDescription>
        </CardHeader>
        <CardContent>
          {auditLogs.logs?.length > 0 ? (
            <div className="space-y-3">
              {auditLogs.logs.map((log, index) => (
                <div key={index} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg border">
                  <div className={`p-2 rounded-full ${getActionBadgeColor(log.action)}`}>
                    {log.action.includes('login') ? <Key className="h-4 w-4" /> : 
                     log.action.includes('ban') ? <Ban className="h-4 w-4" /> :
                     log.action.includes('extend') ? <CalendarPlus className="h-4 w-4" /> :
                     <Settings className="h-4 w-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={getActionBadgeColor(log.action)}>
                        {formatAction(log.action)}
                      </Badge>
                      <span className="text-sm text-gray-500">
                        {new Date(log.timestamp || log.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-900">
                      Admin: {log.admin_email || 'Unknown'}
                    </p>
                    {log.target_email && (
                      <p className="text-sm text-gray-600">
                        Target: {log.target_email}
                      </p>
                    )}
                    {log.ip_address && (
                      <p className="text-xs text-gray-400 mt-1">
                        IP: {log.ip_address}
                      </p>
                    )}
                    {log.details && (
                      <p className="text-xs text-gray-500 mt-1 bg-gray-100 p-2 rounded">
                        {typeof log.details === 'string' ? log.details : JSON.stringify(log.details)}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <Shield className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No audit logs found</p>
              <p className="text-sm">Activity will appear here when admins perform actions</p>
            </div>
          )}

          {/* Pagination */}
          {auditLogs.pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6 pt-6 border-t">
              <Button 
                variant="outline" 
                size="sm" 
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                Previous
              </Button>
              <span className="text-sm text-gray-600">
                Page {currentPage} of {auditLogs.pages}
              </span>
              <Button 
                variant="outline" 
                size="sm" 
                disabled={currentPage === auditLogs.pages}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Audit Log Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="font-medium mb-1">What is logged?</p>
              <ul className="text-gray-600 space-y-1">
                <li>• All login attempts (success & failed)</li>
                <li>• Company ban/unban actions</li>
                <li>• Subscription changes</li>
                <li>• Admin user management</li>
              </ul>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="font-medium mb-1">Data retained</p>
              <ul className="text-gray-600 space-y-1">
                <li>• IP addresses</li>
                <li>• Timestamps (UTC)</li>
                <li>• User agent information</li>
                <li>• Action details & context</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ============== MODALS ==============

const ExtendSubscriptionModal = ({ company, onClose, onExtend }) => {
  const [customDate, setCustomDate] = useState('');
  
  if (!company) return null;

  return (
    <Dialog open={!!company} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Extend Subscription</DialogTitle>
          <DialogDescription>
            Extend subscription for {company.name || company.company_name}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <Label className="text-sm font-medium mb-3 block">Quick Add</Label>
            <div className="grid grid-cols-4 gap-2">
              {[1, 3, 6, 12].map((months) => (
                <Button
                  key={months}
                  variant="outline"
                  onClick={() => onExtend(company.id, months)}
                >
                  {months} {months === 1 ? 'Month' : 'Months'}
                </Button>
              ))}
            </div>
          </div>
          
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-gray-500">Or select date</span>
            </div>
          </div>
          
          <div>
            <Label>Custom End Date</Label>
            <div className="flex gap-2 mt-2">
              <Input
                type="date"
                value={customDate}
                onChange={(e) => setCustomDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
              <Button 
                onClick={() => customDate && onExtend(company.id, null, customDate)}
                disabled={!customDate}
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const BanCompanyModal = ({ company, onClose, onBan }) => {
  const [reason, setReason] = useState('');
  
  if (!company) return null;

  return (
    <Dialog open={!!company} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-red-600">Ban Company</DialogTitle>
          <DialogDescription>
            Ban {company.name} for 30 days. Data will be retained and can be unbanned.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <Label>Reason for ban</Label>
            <textarea
              className="w-full mt-2 p-3 border rounded-lg resize-none"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for banning this company..."
            />
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button variant="destructive" onClick={() => onBan(company.id, reason)}>
              <Ban className="h-4 w-4 mr-2" />
              Ban Company
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CreateAdminModal = ({ open, onClose, roles, token, onSuccess }) => {
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: 'support'
  });
  const [loading, setLoading] = useState(false);
  const [createdAdmin, setCreatedAdmin] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/superadmin/admins`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      
      setCreatedAdmin(data);
      toast.success('Admin created');
    } catch (error) {
      toast.error(error.message || 'Failed to create admin');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ email: '', first_name: '', last_name: '', phone: '', role: 'support' });
    setCreatedAdmin(null);
    onClose();
    if (createdAdmin) onSuccess();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Admin User</DialogTitle>
          <DialogDescription>
            Add a new platform administrator
          </DialogDescription>
        </DialogHeader>
        
        {createdAdmin ? (
          <div className="py-4 text-center">
            <CheckCircle className="mx-auto h-12 w-12 text-green-500 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Admin Created!</h3>
            <p className="text-gray-600 mb-4">Temporary password:</p>
            <code className="bg-gray-100 px-4 py-2 rounded text-lg font-mono">
              {createdAdmin.temp_password}
            </code>
            <p className="text-sm text-gray-500 mt-4">
              Share this with the admin. They will be asked to change it on first login.
            </p>
            <Button className="mt-4 w-full" onClick={handleClose}>Done</Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Last Name</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                />
              </div>
            </div>
            
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
            
            <div>
              <Label>Phone</Label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
            
            <div>
              <Label>Role</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(roles.predefined_roles || {}).map(([key, role]) => (
                    <SelectItem key={key} value={key}>{role.name}</SelectItem>
                  ))}
                  {(roles.custom_roles || []).map((role) => (
                    <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={handleClose}>Cancel</Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Creating...' : 'Create Admin'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

const CreateRoleModal = ({ open, onClose, token, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    level: 50,
    description: '',
    permissions: []
  });
  const [loading, setLoading] = useState(false);

  const allPermissions = [
    'view_stats', 'view_companies', 'manage_companies', 'view_users',
    'manage_users', 'view_subscriptions', 'manage_subscriptions',
    'view_activity', 'manage_contacts', 'view_payments', 'manage_inventory'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/superadmin/roles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (!res.ok) throw new Error('Failed');
      
      toast.success('Role created');
      setFormData({ name: '', level: 50, description: '', permissions: [] });
      onSuccess();
    } catch (error) {
      toast.error('Failed to create role');
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = (perm) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter(p => p !== perm)
        : [...prev.permissions, perm]
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Custom Role</DialogTitle>
          <DialogDescription>
            Define a new role with custom permissions
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div>
            <Label>Role Name</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Content Manager"
              required
            />
          </div>
          
          <div>
            <Label>Level (1-100)</Label>
            <Input
              type="number"
              min={1}
              max={100}
              value={formData.level}
              onChange={(e) => setFormData({ ...formData, level: parseInt(e.target.value) })}
            />
          </div>
          
          <div>
            <Label>Description</Label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of this role"
            />
          </div>
          
          <div>
            <Label className="mb-2 block">Permissions</Label>
            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border rounded">
              {allPermissions.map((perm) => (
                <label key={perm} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.permissions.includes(perm)}
                    onChange={() => togglePermission(perm)}
                    className="rounded"
                  />
                  <span className="text-sm capitalize">{perm.replace(/_/g, ' ')}</span>
                </label>
              ))}
            </div>
          </div>
          
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create Role'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SuperAdminDashboard;

// ============== TICKETS TAB ==============
const TicketsTab = ({ token }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replies, setReplies] = useState([]);
  const [replyMessage, setReplyMessage] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, priorityFilter]);

  const fetchTickets = async () => {
    try {
      let url = `${API_URL}/api/superadmin/tickets?`;
      if (statusFilter && statusFilter !== 'all') url += `status=${statusFilter}&`;
      if (priorityFilter && priorityFilter !== 'all') url += `priority=${priorityFilter}&`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setTickets(data.tickets || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const openTicket = async (ticket) => {
    setSelectedTicket(ticket);
    try {
      const res = await fetch(`${API_URL}/api/superadmin/tickets/${ticket.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setSelectedTicket(data.ticket);
      setReplies(data.replies || []);
    } catch (error) {
      console.error('Error loading ticket:', error);
    }
  };

  const handleReply = async () => {
    if (!replyMessage.trim()) return;
    setSubmitting(true);
    
    try {
      await fetch(`${API_URL}/api/superadmin/tickets/${selectedTicket.id}/reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          message: replyMessage,
          is_internal: isInternal
        })
      });
      
      toast.success(isInternal ? 'Internal note added' : 'Reply sent');
      setReplyMessage('');
      setIsInternal(false);
      openTicket(selectedTicket);
      fetchTickets();
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSubmitting(false);
    }
  };

  const updateStatus = async (status) => {
    try {
      await fetch(`${API_URL}/api/superadmin/tickets/${selectedTicket.id}/status?status=${status}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Status updated to ${status}`);
      openTicket(selectedTicket);
      fetchTickets();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteTicket = async (ticketId) => {
    if (!window.confirm('Are you sure you want to delete this ticket? This action cannot be undone.')) {
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/superadmin/tickets/${ticketId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Ticket deleted');
        setSelectedTicket(null);
        fetchTickets();
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      toast.error('Failed to delete ticket');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      open: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-yellow-100 text-yellow-700',
      waiting_response: 'bg-purple-100 text-purple-700',
      resolved: 'bg-green-100 text-green-700',
      closed: 'bg-gray-100 text-gray-700'
    };
    return <Badge className={config[status] || config.open}>{status?.replace('_', ' ')}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const config = {
      low: 'border-gray-300 text-gray-600',
      medium: 'border-blue-300 text-blue-600',
      high: 'border-orange-300 text-orange-600',
      urgent: 'border-red-300 text-red-600 bg-red-50'
    };
    return <Badge variant="outline" className={config[priority] || config.medium}>{priority}</Badge>;
  };

  return (
    <div className="grid grid-cols-3 gap-6" data-testid="tickets-tab">
      {/* Tickets List */}
      <div className="col-span-1 space-y-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between">
              <span>Support Tickets</span>
              <Button variant="ghost" size="sm" onClick={fetchTickets}>
                <RefreshCw size={16} />
              </Button>
            </CardTitle>
            
            <div className="flex gap-2 pt-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="waiting_response">Waiting</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priority</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="max-h-[600px] overflow-y-auto space-y-2">
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : tickets.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No tickets found</div>
            ) : (
              tickets.map((ticket) => (
                <div
                  key={ticket.id}
                  className={`p-3 border rounded-lg cursor-pointer hover:shadow-md transition-all ${
                    selectedTicket?.id === ticket.id ? 'ring-2 ring-primary' : ''
                  }`}
                  onClick={() => openTicket(ticket)}
                >
                  <div className="flex items-center gap-2 mb-1">
                    {getStatusBadge(ticket.status)}
                    {getPriorityBadge(ticket.priority)}
                  </div>
                  <p className="font-medium text-sm truncate">{ticket.subject}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {ticket.company_name} • {new Date(ticket.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* Ticket Details */}
      <div className="col-span-2">
        {selectedTicket ? (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle>{selectedTicket.subject}</CardTitle>
                  <CardDescription>
                    #{selectedTicket.id?.substring(0, 8).toUpperCase()} • 
                    {selectedTicket.company?.name || selectedTicket.company_name} • 
                    {selectedTicket.created_by_email}
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  {getStatusBadge(selectedTicket.status)}
                  {getPriorityBadge(selectedTicket.priority)}
                </div>
              </div>
              
              <div className="flex gap-2 pt-3">
                <Select onValueChange={updateStatus}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Change Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="waiting_response">Waiting Response</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
                
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => deleteTicket(selectedTicket.id)}
                  data-testid="delete-ticket-btn"
                >
                  <Trash2 size={14} className="mr-1" />
                  Delete
                </Button>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-4">
              {/* Original Message */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-semibold">{selectedTicket.created_by_name}</span>
                  <Badge variant="outline" className="text-xs">Customer</Badge>
                  <span className="text-xs text-gray-500">
                    {new Date(selectedTicket.created_at).toLocaleString()}
                  </span>
                </div>
                <p className="whitespace-pre-wrap">{selectedTicket.description}</p>
              </div>

              {/* Replies */}
              <div className="max-h-[300px] overflow-y-auto space-y-3">
                {replies.map((reply) => (
                  <div
                    key={reply.id}
                    className={`p-3 rounded-lg ${
                      reply.is_internal 
                        ? 'bg-yellow-50 border border-yellow-200' 
                        : reply.user_role === 'admin' 
                          ? 'bg-green-50 border-l-4 border-green-500' 
                          : 'bg-blue-50'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm">{reply.user_name}</span>
                      {reply.is_internal && <Badge className="bg-yellow-100 text-yellow-700 text-xs">Internal Note</Badge>}
                      {reply.user_role === 'admin' && !reply.is_internal && <Badge className="bg-green-100 text-green-700 text-xs">Support</Badge>}
                      <span className="text-xs text-gray-500">
                        {new Date(reply.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{reply.message}</p>
                  </div>
                ))}
              </div>

              {/* Reply Form */}
              {selectedTicket.status !== 'closed' && (
                <div className="border-t pt-4 space-y-3">
                  <textarea
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    placeholder="Type your reply..."
                    rows={3}
                    className="w-full p-3 border rounded-lg resize-none"
                  />
                  <div className="flex justify-between items-center">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={isInternal}
                        onChange={(e) => setIsInternal(e.target.checked)}
                        className="rounded"
                      />
                      Internal note (not visible to customer)
                    </label>
                    <Button onClick={handleReply} disabled={!replyMessage.trim() || submitting}>
                      <MessageSquare size={16} className="mr-2" />
                      {submitting ? 'Sending...' : isInternal ? 'Add Note' : 'Send Reply'}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="py-20 text-center text-gray-500">
              <HelpCircle className="mx-auto mb-4 text-gray-300" size={48} />
              <p>Select a ticket to view details</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// ============== MESSAGING TAB ==============
const MessagingTab = ({ token, companies }) => {
  const [activeSubTab, setActiveSubTab] = useState('compose');
  const [templates, setTemplates] = useState([]);
  const [systemTemplates, setSystemTemplates] = useState([]);
  const [messageLogs, setMessageLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  
  // Compose form state
  const [messageType, setMessageType] = useState('email');
  const [recipients, setRecipients] = useState('all');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [customEmail, setCustomEmail] = useState('');
  const [customPhone, setCustomPhone] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  // System email templates that can be edited
  const systemEmailTypes = [
    { id: 'welcome', name: 'Welcome Email', description: 'Sent when a company registers' },
    { id: 'verification', name: 'Email Verification', description: 'Email verification link' },
    { id: 'password_reset', name: 'Password Reset', description: 'Password reset instructions' },
    { id: 'login_code', name: 'Login Code', description: 'Mobile app login code' },
    { id: 'subscription_created', name: 'Subscription Created', description: 'New subscription confirmation' },
    { id: 'subscription_cancelled', name: 'Subscription Cancelled', description: 'Subscription cancellation notice' },
    { id: 'trial_ending', name: 'Trial Ending Soon', description: 'Trial period ending reminder' },
    { id: 'payment_failed', name: 'Payment Failed', description: 'Payment failure notification' },
    { id: 'job_created', name: 'Job Created', description: 'New job confirmation' },
    { id: 'job_status_update', name: 'Job Status Update', description: 'Job status change notification' },
    { id: 'support_ticket_reply', name: 'Support Ticket Reply', description: 'Reply to support ticket' },
  ];

  useEffect(() => {
    if (activeSubTab === 'templates' || activeSubTab === 'system') {
      fetchTemplates();
    } else if (activeSubTab === 'logs') {
      fetchLogs();
    }
  }, [activeSubTab]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/superadmin/message-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/superadmin/message-logs?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMessageLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error('Please fill in subject and message');
      return;
    }

    setSending(true);
    try {
      const payload = {
        type: messageType,
        recipients: recipients,
        company_id: selectedCompany || null,
        custom_email: customEmail || null,
        custom_phone: customPhone || null,
        subject: subject,
        message: message
      };

      const res = await fetch(`${API_URL}/api/superadmin/send-broadcast`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Message sent to ${data.sent_count} recipients!`);
        setSubject('');
        setMessage('');
        setCustomEmail('');
        setCustomPhone('');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to send message');
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const generatePreview = (template) => {
    // Replace placeholders with sample data
    let preview = template.content || template.body || message;
    const sampleData = {
      '{{company_name}}': 'Acme Removals',
      '{{customer_name}}': 'John Smith',
      '{{job_number}}': 'JOB-2026-001',
      '{{job_date}}': 'January 15, 2026',
      '{{amount}}': '£450.00',
      '{{driver_name}}': 'Mike Johnson',
      '{{eta}}': '2:30 PM',
      '{{tracking_link}}': 'https://track.movematebend.co.uk/abc123',
      '{{login_code}}': '1234',
      '{{verification_link}}': 'https://admin.movematebend.co.uk/verify/abc123',
      '{{reset_link}}': 'https://admin.movematebend.co.uk/reset/abc123',
    };

    Object.entries(sampleData).forEach(([key, value]) => {
      preview = preview.replace(new RegExp(key, 'g'), value);
    });

    setPreviewHtml(preview);
    setShowPreview(true);
  };

  const saveTemplate = async (template) => {
    try {
      const res = await fetch(`${API_URL}/api/superadmin/message-templates/${template.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(template)
      });

      if (res.ok) {
        toast.success('Template saved');
        fetchTemplates();
      } else {
        toast.error('Failed to save template');
      }
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  return (
    <div className="space-y-6" data-testid="messaging-tab">
      {/* Sub-tabs */}
      <div className="flex gap-2 border-b pb-2">
        {[
          { id: 'compose', label: 'Compose Message', icon: <Send size={16} /> },
          { id: 'templates', label: 'Custom Templates', icon: <FileText size={16} /> },
          { id: 'system', label: 'System Emails', icon: <Mail size={16} /> },
          { id: 'logs', label: 'Message Logs', icon: <History size={16} /> },
        ].map(tab => (
          <Button
            key={tab.id}
            variant={activeSubTab === tab.id ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveSubTab(tab.id)}
            className="flex items-center gap-2"
          >
            {tab.icon}
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Compose Message */}
      {activeSubTab === 'compose' && (
        <div className="grid grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Compose Broadcast Message</CardTitle>
              <CardDescription>Send SMS or Email to companies or customers</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Message Type */}
              <div>
                <Label>Message Type</Label>
                <div className="flex gap-2 mt-1">
                  <Button
                    variant={messageType === 'email' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setMessageType('email')}
                  >
                    <Mail size={16} className="mr-2" />
                    Email
                  </Button>
                  <Button
                    variant={messageType === 'sms' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setMessageType('sms')}
                  >
                    <Smartphone size={16} className="mr-2" />
                    SMS
                  </Button>
                  <Button
                    variant={messageType === 'both' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setMessageType('both')}
                  >
                    Both
                  </Button>
                </div>
              </div>

              {/* Recipients */}
              <div>
                <Label>Recipients</Label>
                <Select value={recipients} onValueChange={setRecipients}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Companies</SelectItem>
                    <SelectItem value="active">Active Subscribers Only</SelectItem>
                    <SelectItem value="trial">Trial Users Only</SelectItem>
                    <SelectItem value="company">Specific Company</SelectItem>
                    <SelectItem value="custom">Custom Recipient</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {recipients === 'company' && (
                <div>
                  <Label>Select Company</Label>
                  <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose company..." />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {recipients === 'custom' && (
                <div className="space-y-2">
                  {(messageType === 'email' || messageType === 'both') && (
                    <div>
                      <Label>Email Address</Label>
                      <Input
                        type="email"
                        value={customEmail}
                        onChange={(e) => setCustomEmail(e.target.value)}
                        placeholder="email@example.com"
                      />
                    </div>
                  )}
                  {(messageType === 'sms' || messageType === 'both') && (
                    <div>
                      <Label>Phone Number</Label>
                      <Input
                        type="tel"
                        value={customPhone}
                        onChange={(e) => setCustomPhone(e.target.value)}
                        placeholder="+447..."
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Subject (for email) */}
              {(messageType === 'email' || messageType === 'both') && (
                <div>
                  <Label>Subject</Label>
                  <Input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Message subject..."
                  />
                </div>
              )}

              {/* Message */}
              <div>
                <Label>Message</Label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your message here..."
                  rows={6}
                  className="w-full p-3 border rounded-lg resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Available placeholders: {'{'}{'{'} company_name {'}'}{'}'},  {'{'}{'{'} customer_name {'}'}{'}'}, etc.
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => generatePreview({ content: message })}
                  disabled={!message.trim()}
                >
                  <Eye size={16} className="mr-2" />
                  Preview
                </Button>
                <Button
                  onClick={handleSendMessage}
                  disabled={sending || !message.trim()}
                  className="flex-1"
                >
                  <Send size={16} className="mr-2" />
                  {sending ? 'Sending...' : 'Send Message'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Recipient Count</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold text-primary">
                  {recipients === 'all' ? companies.length :
                   recipients === 'active' ? companies.filter(c => c.subscription_status === 'active').length :
                   recipients === 'trial' ? companies.filter(c => c.subscription_status === 'trial').length :
                   recipients === 'company' ? 1 :
                   recipients === 'custom' ? 1 : 0}
                </div>
                <p className="text-sm text-gray-500">
                  {recipients === 'all' ? 'All registered companies' :
                   recipients === 'active' ? 'Companies with active subscriptions' :
                   recipients === 'trial' ? 'Companies in trial period' :
                   recipients === 'company' ? 'Selected company' :
                   'Custom recipient'}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Message Templates</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {[
                    { label: 'Maintenance Notice', text: 'Dear {{company_name}}, we will be performing scheduled maintenance...' },
                    { label: 'New Feature', text: 'Exciting news! We have released a new feature...' },
                    { label: 'Payment Reminder', text: 'This is a friendly reminder that your payment is due...' },
                  ].map((t, i) => (
                    <Button
                      key={i}
                      variant="outline"
                      size="sm"
                      className="w-full justify-start text-left"
                      onClick={() => {
                        setSubject(t.label);
                        setMessage(t.text);
                      }}
                    >
                      {t.label}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Custom Templates */}
      {activeSubTab === 'templates' && (
        <Card>
          <CardHeader>
            <CardTitle>Custom Message Templates</CardTitle>
            <CardDescription>Create and manage reusable message templates</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : templates.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="mx-auto mb-4" size={48} />
                <p>No custom templates yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {templates.map(template => (
                  <div key={template.id} className="p-4 border rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold">{template.name}</h3>
                        <p className="text-sm text-gray-500">{template.description}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => generatePreview(template)}
                        >
                          <Eye size={14} />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedTemplate(template)}
                        >
                          <Edit size={14} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* System Email Templates */}
      {activeSubTab === 'system' && (
        <Card>
          <CardHeader>
            <CardTitle>System Email Templates</CardTitle>
            <CardDescription>Edit the default system emails sent by MoveMate</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              {systemEmailTypes.map(template => (
                <div
                  key={template.id}
                  className="p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setSelectedTemplate(template)}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Mail size={20} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{template.name}</h3>
                      <p className="text-sm text-gray-500">{template.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Message Logs */}
      {activeSubTab === 'logs' && (
        <Card>
          <CardHeader>
            <CardTitle>Message History</CardTitle>
            <CardDescription>All sent messages and broadcasts</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : messageLogs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <History className="mx-auto mb-4" size={48} />
                <p>No messages sent yet</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {messageLogs.map((log, i) => (
                  <div key={i} className="p-3 border rounded-lg flex items-center gap-4">
                    <div className={`p-2 rounded-full ${log.type === 'sms' ? 'bg-green-100' : 'bg-blue-100'}`}>
                      {log.type === 'sms' ? <Smartphone size={16} className="text-green-600" /> : <Mail size={16} className="text-blue-600" />}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{log.subject || log.message?.substring(0, 50)}</p>
                      <p className="text-sm text-gray-500">
                        To: {log.recipient} • {new Date(log.sent_at).toLocaleString()}
                      </p>
                    </div>
                    <Badge variant={log.status === 'sent' ? 'default' : 'destructive'}>
                      {log.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Message Preview</DialogTitle>
            <DialogDescription>This is how the message will appear to recipients</DialogDescription>
          </DialogHeader>
          <div className="border rounded-lg p-4 bg-white overflow-auto max-h-[500px]">
            <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
